/*
 * "What is near this venue", without Google Places.
 *
 * This is the one capability in the map migration that has no drop-in
 * replacement (docs/osm-self-hosting.md, section 5). Places nearbySearch is a
 * product; OSM has no equivalent product. What it has is the raw data and a
 * query language over it, so "restaurants within 500 metres" stops being a
 * feature someone sells and becomes a query.
 *
 * The Google composable this mirrors is useGoogleMap.ts. Deliberately NOT an
 * extension of it: that one owns google.maps.Marker instances and mutates a
 * map object, while this one only produces plain data and lets the map seam
 * draw it. Same panel, same four filters, same 500m, different half of the
 * seam -- which is the point of having a seam.
 *
 * Nothing here runs when MAP_POI_URL is unset, which is the default. See the
 * note on that constant for why an empty default is the right one.
 */
import { MAP_POI_RADIUS, MAP_POI_URL } from '@/config/map';
/*
 * Google's place types do not map one-to-one onto OSM tags, and pretending
 * they do is how a panel ends up quietly missing half its results.
 *
 *   restaurant       amenity=restaurant, plus fast_food, which OSM keeps
 *                    separate and a user does not
 *   cafe             amenity=cafe, plus bar and pub -- "somewhere to sit with
 *                    a drink", which is what the icon promises
 *   parking          amenity=parking
 *   transit_station  OSM splits this across the railway and highway schemes:
 *                    railway=station and =halt are trains, highway=bus_stop
 *                    and public_transport=station cover the rest
 *
 * Each entry is a list of [key, value] pairs; any match puts the element in
 * that category.
 */
const OSM_TAGS = {
    restaurant: [
        ['amenity', 'restaurant'],
        ['amenity', 'fast_food']
    ],
    cafe: [
        ['amenity', 'cafe'],
        ['amenity', 'bar'],
        ['amenity', 'pub']
    ],
    parking: [['amenity', 'parking']],
    transit_station: [
        ['railway', 'station'],
        ['railway', 'halt'],
        ['highway', 'bus_stop'],
        ['public_transport', 'station']
    ]
};
/*
 * `nwr` rather than `node`, because a restaurant is often mapped as the
 * building's way and a car park almost always is. Asking only for nodes
 * returns a fraction of what is there and looks like sparse data rather than a
 * wrong query.
 *
 * `out center` gives every way and relation a single representative
 * coordinate, so the caller does not have to reduce a polygon to a point.
 */
const buildQuery = (type, lat, lng) => {
    const clauses = OSM_TAGS[type]
        .map(([key, value]) => `nwr["${key}"="${value}"](around:${MAP_POI_RADIUS},${lat},${lng});`)
        .join('');
    return `[out:json][timeout:20];(${clauses});out center 60;`;
};
export const fetchNearby = async (type, lat, lng) => {
    if (!MAP_POI_URL)
        return [];
    try {
        const response = await fetch(MAP_POI_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: buildQuery(type, lat, lng)
        });
        if (!response.ok)
            return [];
        const payload = await response.json();
        return (payload.elements || [])
            .map((element) => {
            // A node carries lat/lon directly; a way or relation carries `center`
            // because the query asked for `out center`.
            const point = element.center
                ? { lat: element.center.lat, lng: element.center.lon }
                : { lat: element.lat, lng: element.lon };
            if (typeof point.lat !== 'number' || typeof point.lng !== 'number') {
                return null;
            }
            return {
                id: element.id,
                name: element.tags?.name || '',
                type,
                lat: point.lat,
                lng: point.lng
            };
        })
            .filter(Boolean);
    }
    catch {
        // A POI panel is context, never the point of the page. An Overpass that is
        // slow, rate-limiting or unreachable must leave the venue's own map
        // working rather than take the panel down with it.
        return [];
    }
};
/*
 * Ids have to be unique across the marker list, and the venue in the middle of
 * this panel is `venue_id`, a small positive integer. OSM ids are large
 * positive integers and would not collide today, but "would not collide today"
 * is not a guarantee -- so POIs are negated, which cannot collide with a
 * venue id by construction.
 */
export const toMarker = (place) => ({
    id: -place.id,
    position: { lat: place.lat, lng: place.lng },
    title: place.name,
    variant: 'poi'
});
export const isNearbyEnabled = () => Boolean(MAP_POI_URL);
